/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The three per-page overrides of the admin SEO tab — `seo.<pageKey>_og_image`,
 * `seo.<pageKey>_noindex` and `seo.<pageKey>_custom_schema` — resolved and
 * applied ONE way by the head of every page, injectHead in src/lib/seoHead.js —
 * the baked head (scripts/prerender.mjs) and the request-time one
 * (server/seoMeta.ts) alike.
 *
 * Until 2026-09-23 the mirrors applied them to kennisbank articles only
 * (`article_<id>`). On every static page and on both templates the tab's share
 * image, noindex and extra JSON-LD existed only after hydration, in
 * src/hooks/useSEO.ts — and the readers they exist for do not run JavaScript:
 * Facebook, LinkedIn, X and WhatsApp showed the default /og-image.jpg for every
 * static page, and a static noindex was a JS-only noindex. The head now
 * resolves the page key the client resolves and applies the article rules to it.
 *
 * Plain ESM JavaScript because the bake is plain Node and cannot load
 * TypeScript (the indexPolicy.js pattern). The client applies the same rules
 * in useSEO.ts and ArticlePage.tsx; server/__tests__/pageSeoOverrides.test.ts
 * pins that the keys the pages read are the keys resolved here, and that the
 * head calls this module rather than a copy of it.
 */

import { absoluteSeoUrl, jsonLdScriptBody, SEO_HOST } from './articleSeo.js';
import { AUTHOR_SEGMENT } from './authorSlug.js';
import { BEGRIPPEN_SEGMENT } from './begrippen.js';
import { CHARGER_BRANDS_SEGMENT } from './chargerCatalog.js';
import { escapeHtml } from './htmlEscape.js';
import { CSP_NONCE_ATTR } from './cspNonce.js';
import { PAGE_SEO_KEYS, seoRowKeyOf } from './pageSeoKeys.js';
import {
  SHARE_IMAGE_HEIGHT,
  SHARE_IMAGE_TYPE,
  SHARE_IMAGE_WIDTH,
  shareImagePathOf,
} from './shareImage.js';

/** The SEO-tab row of every /auteur/:slug page — one template. */
export const AUTHOR_SEO_PAGE_KEY = 'author';

/**
 * The SEO-tab row of every /begrippen/:slug page — one template. Its title and
 * description (`seo.begrip_title` / `seo.begrip_description`) are filled per
 * term by begripSeoTexts in src/lib/begrippen.js.
 */
export const BEGRIP_SEO_PAGE_KEY = 'begrip';

/**
 * The SEO-tab row of every /laadpalen/:slug brand page — one template, the row
 * ChargerBrandPage's useSEO derives from `charger_brand_title`. Its title and
 * description (`seo.charger_brand_title` / `seo.charger_brand_description`)
 * are filled per brand by chargerBrandSeoTexts in src/lib/chargerCatalog.js,
 * after the brand's own `seoTitle` / `seoDescription`. The hub keeps its own
 * row, `charger_brands` (its PAGE_SEO_KEYS entry).
 */
export const CHARGER_BRAND_SEO_PAGE_KEY = 'charger_brand';

/**
 * The SEO-tab row of every /laadpalen/:slug/:model page (2026-10-21) — one
 * template for the models with a page of their own (the ones Laadpaaldirect
 * sells with a built-in MID meter, src/lib/chargerModelPages.js). Its title
 * and description (`seo.charger_model_title` / `seo.charger_model_description`)
 * are filled per model by chargerModelSeoTexts in src/lib/chargerCatalog.js.
 */
export const CHARGER_MODEL_SEO_PAGE_KEY = 'charger_model';

/**
 * The robots value of the editor's explicit opt-out. Stronger than the
 * thin-page `noindex, follow` (src/lib/indexPolicy.js) on purpose: an editor
 * who switches a page off withholds its link equity too. It therefore wins
 * over every automatic rule — in the head, in useSEO and in ArticlePage.
 */
export const EXPLICIT_NOINDEX_ROBOTS = 'noindex,nofollow';

/**
 * Id of a static page's extra JSON-LD: the id useSEO manages, so the hook
 * replaces the baked node in place after hydration instead of adding a second
 * one. An article's lives under `jsonld-article-custom` (buildArticleHeadExtras
 * in src/lib/articleSeo.js, managed by ArticlePage).
 */
export const PAGE_CUSTOM_JSONLD_ID = 'jsonld-custom';

/**
 * The SEO-tab row whose overrides apply to a route, or null when the tab has
 * none for it. `article`, `author`, `begrip`, `chargerBrand` and
 * `chargerModel` are the RESOLVED records — null when the slug does not
 * resolve (or the brand is unpublished, or the model has no page), which
 * makes the route a 404.
 *
 *   /kennisbank/:slug     → `article_<id>`
 *   /auteur/:slug         → `author`
 *   /begrippen/:slug      → `begrip` (one row, every term page)
 *   /laadpalen/:slug      → `charger_brand` (one row, every brand page)
 *   /laadpalen/:slug/:model → `charger_model` (one row, every model page)
 *   a static route        → its PAGE_SEO_KEYS entry's `pageKey` (only
 *                           /geen-laadpaal names one: `geen_laadpaal`, whose
 *                           title key is `geen_laadpaal_partner_title_v3`), else
 *                           its `seo.<key>_title` key minus `_title` — what
 *                           useSEO derives from the key its page passes
 *   anything else         → null: a provider profile, a matchup, a client-only
 *                           view, a retired /geen-laadpaal/<provincie> address
 *                           (the template row `geen_laadpaal_province` went
 *                           with the province pages on 2026-10-07), an
 *                           unknown path. Never the homepage's row:
 *                           the TITLE lookup falls back to it for an unknown
 *                           segment (pageSeoKeys), but an override must not —
 *                           the homepage's extra JSON-LD on a 404 would be false.
 *
 * @param {{segment?: string, slug?: string, article?: any, author?: any, begrip?: any, chargerBrand?: any, chargerModel?: any}} route
 * @returns {string | null}
 */
export function seoOverridePageKey(route) {
  const {
    segment = '',
    slug = '',
    article = null,
    author = null,
    begrip = null,
    chargerBrand = null,
    chargerModel = null,
  } = route ?? {};
  if (slug) {
    if (segment === 'kennisbank' && article?.id) return `article_${article.id}`;
    if (segment === AUTHOR_SEGMENT && author) return AUTHOR_SEO_PAGE_KEY;
    if (segment === BEGRIPPEN_SEGMENT && begrip) return BEGRIP_SEO_PAGE_KEY;
    // A model page also has its brand's payload: the model decides first.
    if (segment === CHARGER_BRANDS_SEGMENT && chargerModel) return CHARGER_MODEL_SEO_PAGE_KEY;
    if (segment === CHARGER_BRANDS_SEGMENT && chargerBrand) return CHARGER_BRAND_SEO_PAGE_KEY;
    return null;
  }
  if (!Object.prototype.hasOwnProperty.call(PAGE_SEO_KEYS, segment)) return null;
  return seoRowKeyOf(PAGE_SEO_KEYS[segment]);
}

/**
 * The overrides of one SEO-tab row, parsed by the rules the article branch
 * has always baked with:
 *   - og image: a non-blank string, trimmed. Blank means "none" — the tab's
 *     Verwijder writes ''.
 *   - noindex: `true` or `'true'`. The tab writes a boolean, older data holds
 *     the string, and the client's t() stringifies both.
 *   - custom schema: only when it parses as JSON, re-serialised compact. A
 *     value that does not parse is skipped, never baked half-way — the client
 *     skips it too, and the tab marks it "Ongeldige JSON".
 *
 * @param {Record<string, unknown> | null | undefined} seo  The merged `seo` namespace of one language.
 * @param {string | null} pageKey  From {@link seoOverridePageKey}.
 * @returns {{ ogImage: string | null, noindex: boolean, customJsonLd: string | null }}
 */
export function readPageSeoOverrides(seo, pageKey) {
  if (!pageKey || !seo || typeof seo !== 'object') {
    return { ogImage: null, noindex: false, customJsonLd: null };
  }
  const og = seo[`${pageKey}_og_image`];
  const noindex = seo[`${pageKey}_noindex`];
  const custom = seo[`${pageKey}_custom_schema`];
  let customJsonLd = null;
  if (typeof custom === 'string' && custom.trim()) {
    try {
      customJsonLd = JSON.stringify(JSON.parse(custom));
    } catch {
      customJsonLd = null;
    }
  }
  return {
    ogImage: typeof og === 'string' && og.trim() ? og.trim() : null,
    noindex: noindex === true || noindex === 'true',
    customJsonLd,
  };
}

/**
 * The position of the share crop a page's og:image chain ends in — the chain
 * the head writes as `overrides.ogImage || <the article's hero>`: the
 * article hero's own position (`heroImageFocus`, set by dragging it in its
 * frame) when the page shares that hero, '' (the centre) when it shares the
 * SEO tab's image, which has no position. ArticlePage applies the same rule
 * after hydration.
 *
 * @param {{ ogImage: string | null }} overrides  From {@link readPageSeoOverrides}.
 * @param {{ heroImageFocus?: unknown } | null | undefined} article  The resolved article, if any.
 * @returns {string}
 */
export function ogImageFocusOf(overrides, article) {
  if (overrides?.ogImage) return '';
  const focus = article?.heroImageFocus;
  return typeof focus === 'string' ? focus : '';
}

/**
 * Point the template's share-image tags at a page's own image: an absolute URL
 * (scrapers do not resolve relative ones) in og:image, og:image:secure_url and
 * twitter:image, and the page headline in og:image:alt.
 *
 * An image the site serves itself (/images, /uploads) is shared as its share
 * crop (src/lib/shareImage.js): a 1200×630 JPEG, so og:image:type/width/height
 * are written for it like for the default. Any other image — another host, an
 * SVG — is shared as it is, and the template's type/width/height are dropped:
 * they describe the default 1200×630 JPEG, not an arbitrary image, and useSEO
 * removes them at hydration too.
 *
 * The crop is cut at `focus` — {@link ogImageFocusOf}: an article hero's
 * position — and from the centre without one (the address is then unchanged).
 *
 * The replacements take functions, not strings, so a `$` in a title or URL
 * is copied as a `$` instead of being read as a replacement pattern.
 *
 * @param {string} html
 * @param {{ image: string, alt: string, host?: string, focus?: unknown }} opts
 * @returns {string}
 */
export function applyOgImageOverride(html, { image, alt, host = SEO_HOST, focus = '' }) {
  const share = shareImagePathOf(image, host, focus);
  // The one escaper of every head value (src/lib/htmlEscape.js). Until
  // 2026-10-14 this module kept its own, which escaped `&` and `"` only, so
  // the alt said `auto's` where og:title said `auto&#39;s`.
  const url = escapeHtml(absoluteSeoUrl(share ?? image, host));
  const altText = escapeHtml(alt);
  const result = html
    .replace(/(<meta property="og:image" content=")[^"]*(")/, (_, a, b) => a + url + b)
    .replace(/(<meta property="og:image:secure_url" content=")[^"]*(")/, (_, a, b) => a + url + b)
    .replace(/(<meta property="og:image:alt" content=")[^"]*(")/, (_, a, b) => a + altText + b)
    .replace(/(<meta name="twitter:image" content=")[^"]*(")/, (_, a, b) => a + url + b);
  if (!share) {
    return result.replace(
      /[ \t]*<meta property="og:image:(?:type|width|height)" content="[^"]*" \/>\r?\n/g,
      ''
    );
  }
  return result
    .replace(
      /(<meta property="og:image:type" content=")[^"]*(")/,
      (_, a, b) => a + SHARE_IMAGE_TYPE + b
    )
    .replace(
      /(<meta property="og:image:width" content=")[^"]*(")/,
      (_, a, b) => a + SHARE_IMAGE_WIDTH + b
    )
    .replace(
      /(<meta property="og:image:height" content=")[^"]*(")/,
      (_, a, b) => a + SHARE_IMAGE_HEIGHT + b
    );
}

/**
 * The description of the site-wide WebSite node in index.html, per language.
 * The template carries the Dutch one; {@link applyBaselineHead} swaps it on an
 * English page. server/__tests__/seoBaseline.test.ts pins that index.html
 * still says `nl` word for word — a reworded template would otherwise stop
 * being swapped without a sound.
 */
export const SITE_DESCRIPTION = Object.freeze({
  nl: 'Onafhankelijke vergelijker voor ERE-certificaten en inboekdienstverleners voor laadpalen in Nederland.',
  en: 'Independent comparison of ERE certificates and ERE registration service providers for EV chargers in the Netherlands.',
});

/**
 * The template-head lines that describe every page but that index.html can
 * only say for the homepage — applied by the head to every route, before
 * {@link applyOgImageOverride} (which sets its own alt for its own image):
 *   - og:image:alt: the page's headline. The template's is the homepage's
 *     Dutch sentence, and until 2026-10-04 every baked page — English ones
 *     too — kept it until useSEO set the title at hydration.
 *   - og:locale:alternate: the other language, or no tag at all on a page
 *     that exists in one language only (an untranslated article or term),
 *     the hreflang rule.
 *   - the WebSite node's description in the page's language.
 *
 * @param {string} html
 * @param {{ lang: 'nl' | 'en', contentLang: 'nl' | 'en', translated: boolean, imageAlt: string }} opts
 * @returns {string}
 */
export function applyBaselineHead(html, { lang, contentLang, translated, imageAlt }) {
  const alt = escapeHtml(imageAlt);
  const other = contentLang === 'nl' ? 'en_US' : 'nl_NL';
  let result = html
    .replace(/(<meta property="og:image:alt" content=")[^"]*(")/, (_, a, b) => a + alt + b)
    .replace(
      /(<meta property="og:locale:alternate" content=")[^"]*(")/,
      (_, a, b) => a + other + b
    );
  if (!translated) {
    result = result.replace(
      /[ \t]*<meta property="og:locale:alternate" content="[^"]*" \/>\r?\n/,
      ''
    );
  }
  if (lang === 'en') result = result.replace(SITE_DESCRIPTION.nl, () => SITE_DESCRIPTION.en);
  return result;
}

/**
 * A static page's extra JSON-LD as a head line, under the client-managed id.
 * No nonce is baked: it would be stale by the next request. The tag carries
 * the nonce placeholder (src/lib/cspNonce.js) instead, and server/spaRenderer.ts
 * (applyNonce) swaps it for the response's nonce, baked file or fallback
 * alike, as on every other JSON-LD node. Every `<` of the body is escaped (jsonLdScriptBody in articleSeo.js), so an
 * editor's text can neither close the tag nor make the browser swallow what
 * follows it.
 *
 * @param {string | null} customJsonLd  From {@link readPageSeoOverrides}.
 * @returns {string | null}
 */
export function pageCustomJsonLdTag(customJsonLd) {
  if (typeof customJsonLd !== 'string' || !customJsonLd.trim()) return null;
  return `  <script type="application/ld+json" id="${PAGE_CUSTOM_JSONLD_ID}" data-source="seo-studio"${CSP_NONCE_ATTR}>${jsonLdScriptBody(customJsonLd)}</script>`;
}
